import React, { useState } from "react";
import { IconButton, Typography, Badge, Button, Menu, MenuItem, ListItemText } from "@material-ui/core";
import WhatsAppAvatar from "../WhatsAppAvatar";
import { Business, Receipt, Assignment, Store, ArrowDropDown } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";
import ErpClientInfoDialog from "../ErpClientInfoDialog";
import ErpDocumentListDialog from "../ErpDocumentListDialog";
import useErpClient from "../../hooks/useErpClient";
import { formatContactName, formatPhoneNumber, isLikelyWhatsAppLid, isPhoneNumber } from "../../utils/formatPhoneNumber";
import erpClientDisplayName from "../../utils/erpClientDisplayName";
import { useContactPresence } from "../../context/ContactPresence/ContactPresenceContext";
import { formatChatPresenceLabel, isTypingPresence } from "../../helpers/formatChatPresence";

const useStyles = makeStyles((theme) => ({
    root: {
        display: "flex",
        alignItems: "center",
        flex: 1,
        minWidth: 0,
        gap: theme.spacing(1.25),
        cursor: "pointer",
        padding: theme.spacing(0.5, 0),
        "&:hover $name": {
            color: theme.palette.primary.main,
        },
    },
    info: {
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        gap: 1,
    },
    name: {
        fontWeight: 600,
        fontSize: "0.9375rem",
        lineHeight: 1.25,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        transition: "color 0.15s ease",
    },
    meta: {
        fontSize: "0.75rem",
        lineHeight: 1.2,
        color: theme.palette.text.secondary,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    erpBadge: {
        color: theme.palette.primary.main,
        fontWeight: 500,
    },
    erpActions: {
        display: "flex",
        gap: 4,
        flexShrink: 0,
    },
    erpBtn: {
        width: 30,
        height: 30,
        padding: 5,
        borderRadius: 8,
        "& .MuiSvgIcon-root": {
            fontSize: 17,
        },
    },
    farmaciasBtn: {
        height: 30,
        whiteSpace: "nowrap",
        textTransform: "none",
        fontSize: "0.75rem",
        padding: "2px 8px",
    },
    erpClient: {
        backgroundColor: "#4A90E2",
        color: "#fff",
        "&:hover": { backgroundColor: "#357ABD" },
    },
    erpFacturas: {
        backgroundColor: "#6B5B95",
        color: "#fff",
        "&:hover": { backgroundColor: "#574A7A" },
    },
    erpOrdenes: {
        backgroundColor: "#E67E22",
        color: "#fff",
        "&:hover": { backgroundColor: "#CF6D17" },
    },
    erpCountBadge: {
        fontSize: "0.65rem",
        fontWeight: 700,
        minWidth: 16,
        height: 16,
        lineHeight: "16px",
    },
    nameRow: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        minWidth: 0,
    },
    presenceBadge: {
        flexShrink: 0,
        fontSize: "0.68rem",
        fontWeight: 600,
        lineHeight: 1.2,
        borderRadius: 8,
        padding: "2px 7px",
        textTransform: "none",
        whiteSpace: "nowrap",
    },
    presenceTyping: {
        backgroundColor: "#e8f5e9",
        color: "#2e7d32",
    },
    presenceOnline: {
        backgroundColor: "#e3f2fd",
        color: "#1565c0",
    },
    presenceLastSeen: {
        backgroundColor: "#f5f5f5",
        color: "#616161",
    },
}));

const TicketInfo = ({
    contact,
    ticket,
    onClick,
    erpClient: erpClientProp,
    erpClients: erpClientsProp,
    onSelectErpClient,
    erpConfigured: erpConfiguredProp,
    erpFrontendUrl: erpFrontendUrlProp,
    erpPendingCounts: erpPendingCountsProp
}) => {
    const classes = useStyles();
    const hook = useErpClient(erpClientProp !== undefined ? null : contact?.number);
    const erpClient = erpClientProp !== undefined ? erpClientProp : hook.erpClient;
    // Farmacias registered in the ERP under this same phone
    const erpClients = (erpClientProp !== undefined ? erpClientsProp : hook.erpClients) || [];
    const selectErpClient = erpClientProp !== undefined ? onSelectErpClient : hook.selectErpClient;
    const [farmaciasAnchor, setFarmaciasAnchor] = useState(null);
    const erpFrontendUrl = erpFrontendUrlProp !== undefined ? erpFrontendUrlProp : hook.erpFrontendUrl;
    const erpLoading = erpClientProp !== undefined ? false : hook.loading;
    const erpPendingCounts =
        erpPendingCountsProp !== undefined
            ? erpPendingCountsProp
            : hook.erpPendingCounts;

    const [showErpDialog, setShowErpDialog] = useState(false);
    const [showFacturasDialog, setShowFacturasDialog] = useState(false);
    const [showOrdenesDialog, setShowOrdenesDialog] = useState(false);

    const presence = useContactPresence(ticket);
    const presenceLabel = formatChatPresenceLabel(presence);

    const displayTitle = erpClient
        ? erpClientDisplayName(erpClient)
        : formatContactName(
            (contact.name && !isLikelyWhatsAppLid(contact.name) ? contact.name : "") ||
            (contact.number && !isLikelyWhatsAppLid(contact.number) ? contact.number : "") ||
            i18n.t("contacts.unknownNumber")
          );

    const headerAvatar = (
        <WhatsAppAvatar
            src={contact.profilePicUrl}
            name={erpClient ? erpClientDisplayName(erpClient) : (contact.name || contact.number)}
            size={36}
        />
    );

    const metaParts = [];
    if (contact.number && isPhoneNumber(contact.number)) {
        metaParts.push(formatPhoneNumber(contact.number));
    }
    if (ticket.user) {
        metaParts.push(
            `${i18n.t("messagesList.header.assignedTo")} ${ticket.user.name}`
        );
    }
    const metaLine = metaParts.join(" · ");

    return (
        <>
            <div className={classes.root} onClick={onClick} role="button" tabIndex={0}>
                {headerAvatar}
                <div className={classes.info}>
                    <div className={classes.nameRow}>
                        <Typography className={classes.name} component="div" noWrap>
                            {displayTitle}
                        </Typography>
                        {presenceLabel && (
                            <span
                                className={`${classes.presenceBadge} ${
                                    isTypingPresence(presence?.presence)
                                        ? classes.presenceTyping
                                        : presence?.presence === "online" || presence?.presence === "paused"
                                        ? classes.presenceOnline
                                        : classes.presenceLastSeen
                                }`}
                            >
                                {presenceLabel}
                            </span>
                        )}
                    </div>
                    {(metaLine || erpClient) && (
                        <Typography className={classes.meta} component="div" noWrap>
                            {erpClient && (
                                <span className={classes.erpBadge}>
                                    ERP · {erpClient.codigo || "Cliente"}
                                    {(metaLine ? " · " : "")}
                                </span>
                            )}
                            {metaLine}
                        </Typography>
                    )}
                </div>
                {!erpLoading && erpClient && (
                    <div
                        className={classes.erpActions}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        role="presentation"
                    >
                        {erpClients.length > 1 && selectErpClient && (
                            <>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    className={classes.farmaciasBtn}
                                    startIcon={<Store />}
                                    endIcon={<ArrowDropDown />}
                                    onClick={(e) => setFarmaciasAnchor(e.currentTarget)}
                                    title={`Este número tiene ${erpClients.length} farmacias en el ERP. Clic para cambiar.`}
                                >
                                    {erpClients.length} farmacias
                                </Button>
                                <Menu
                                    anchorEl={farmaciasAnchor}
                                    open={Boolean(farmaciasAnchor)}
                                    onClose={() => setFarmaciasAnchor(null)}
                                >
                                    {erpClients.map((c) => (
                                        <MenuItem
                                            key={c.clienteId}
                                            selected={c.clienteId === erpClient.clienteId}
                                            onClick={() => {
                                                selectErpClient(c.clienteId);
                                                setFarmaciasAnchor(null);
                                            }}
                                        >
                                            <ListItemText
                                                primary={erpClientDisplayName(c)}
                                                secondary={`ERP · ${c.codigo || c.clienteId}`}
                                            />
                                        </MenuItem>
                                    ))}
                                </Menu>
                            </>
                        )}
                        <IconButton
                            onClick={() => setShowErpDialog(true)}
                            className={`${classes.erpBtn} ${classes.erpClient}`}
                            title="Ver cliente ERP"
                            size="small"
                        >
                            <Business />
                        </IconButton>
                        <IconButton
                            onClick={() => setShowFacturasDialog(true)}
                            className={`${classes.erpBtn} ${classes.erpFacturas}`}
                            title={
                                erpPendingCounts.facturasPendientes > 0
                                    ? `Facturas de venta (${erpPendingCounts.facturasPendientes} con saldo pendiente)`
                                    : "Facturas de venta"
                            }
                            size="small"
                        >
                            <Badge
                                badgeContent={erpPendingCounts.facturasPendientes}
                                color="secondary"
                                overlap="circular"
                                invisible={!erpPendingCounts.facturasPendientes}
                                classes={{ badge: classes.erpCountBadge }}
                            >
                                <Receipt />
                            </Badge>
                        </IconButton>
                        <IconButton
                            onClick={() => setShowOrdenesDialog(true)}
                            className={`${classes.erpBtn} ${classes.erpOrdenes}`}
                            title={
                                erpPendingCounts.ordenesPendientes > 0
                                    ? `Órdenes de venta (${erpPendingCounts.ordenesPendientes} con saldo pendiente)`
                                    : "Órdenes de venta"
                            }
                            size="small"
                        >
                            <Badge
                                badgeContent={erpPendingCounts.ordenesPendientes}
                                color="secondary"
                                overlap="circular"
                                invisible={!erpPendingCounts.ordenesPendientes}
                                classes={{ badge: classes.erpCountBadge }}
                            >
                                <Assignment />
                            </Badge>
                        </IconButton>
                    </div>
                )}
            </div>

            {showErpDialog && erpClient && (
                <ErpClientInfoDialog
                    open={showErpDialog}
                    onClose={() => setShowErpDialog(false)}
                    cliente={erpClient}
                />
            )}

            {showFacturasDialog && erpClient?.clienteId && (
                <ErpDocumentListDialog
                    open={showFacturasDialog}
                    onClose={() => setShowFacturasDialog(false)}
                    type="facturas-venta"
                    clienteId={erpClient.clienteId}
                    clienteName={erpClientDisplayName(erpClient)}
                    erpFrontendUrl={erpFrontendUrl}
                />
            )}

            {showOrdenesDialog && erpClient?.clienteId && (
                <ErpDocumentListDialog
                    open={showOrdenesDialog}
                    onClose={() => setShowOrdenesDialog(false)}
                    type="ordenes"
                    clienteId={erpClient.clienteId}
                    clienteName={erpClientDisplayName(erpClient)}
                    erpFrontendUrl={erpFrontendUrl}
                />
            )}
        </>
    );
};

export default TicketInfo;
